import type { Deck, SlideData } from '@digital-twins/deck-ui/slides/contracts'
import {
  buildResponseImageMap,
  normalizePersonaMapKey,
  type RespondentProfile,
} from '@/features/studies/report/audienceProfiles'

interface QualRespondent {
  name?: string
  label?: string
  imageUrl?: string
  description?: string
}

function normalizeLookupKey(value: unknown): string {
  return normalizePersonaMapKey(String(value || ''))
}

export function collectQualRespondentNames(deck: Deck): Array<string> {
  const names = new Set<string>()

  for (const slide of deck.slides) {
    if (slide.type !== 'qual-analysis') {
      continue
    }

    const qualSlide = slide as Record<string, unknown>
    const featuredRespondent = (qualSlide.featuredQuote as { respondent?: QualRespondent } | undefined)
      ?.respondent
    const themeBlocks = qualSlide.themeBlocks as
      | Array<{ quote?: { respondent?: QualRespondent } }>
      | undefined
    const scopeFeatured = (qualSlide.editorial as { scopeFeatured?: Array<{ respondent?: QualRespondent }> } | undefined)
      ?.scopeFeatured

    for (const respondent of [
      featuredRespondent,
      ...(Array.isArray(themeBlocks)
        ? themeBlocks.map((block) => block.quote?.respondent)
        : []),
      ...(Array.isArray(scopeFeatured)
        ? scopeFeatured.map((person) => person.respondent)
        : []),
    ]) {
      if (!respondent) {
        continue
      }
      for (const key of [respondent.name, respondent.label]) {
        const normalized = String(key || '').trim()
        if (normalized) {
          names.add(normalized)
        }
      }
    }
  }

  return Array.from(names)
}

function buildProfileImageMap(
  profiles: Array<RespondentProfile>,
): Map<string, string> {
  const imageByKey = new Map<string, string>()
  for (const profile of profiles) {
    const imageUrl = String(profile.image_url || '').trim()
    if (!imageUrl) continue
    for (const key of [
      profile.respondent_id,
      profile.name,
      String(profile.respondent_attributes?.persona_name || ''),
    ]) {
      const normalized = normalizeLookupKey(key)
      if (normalized) {
        imageByKey.set(normalized, imageUrl)
      }
    }
  }
  return imageByKey
}

function resolveRespondentImageUrl(
  respondent: QualRespondent | undefined,
  imageByKey: Map<string, string>,
): string | undefined {
  if (!respondent) return undefined
  const existing = String(respondent.imageUrl || '').trim()
  if (existing) return existing

  for (const key of [respondent.name, respondent.label]) {
    const normalized = normalizeLookupKey(key)
    const imageUrl = normalized ? imageByKey.get(normalized) : undefined
    if (imageUrl) return imageUrl
  }
  return undefined
}

function enrichRespondent(
  respondent: QualRespondent | undefined,
  imageByKey: Map<string, string>,
): QualRespondent | undefined {
  if (!respondent) return respondent
  const imageUrl = resolveRespondentImageUrl(respondent, imageByKey)
  if (!imageUrl) return respondent
  return {
    ...respondent,
    imageUrl,
  }
}

function enrichQualAnalysisSlide(
  slide: SlideData,
  imageByKey: Map<string, string>,
): SlideData {
  if (slide.type !== 'qual-analysis') return slide

  const record = slide as Record<string, unknown>
  const featuredQuote = record.featuredQuote as
    | { respondent?: QualRespondent; text?: string }
    | undefined
  const themeBlocks = record.themeBlocks as
    | Array<{ quote?: { respondent?: QualRespondent; text?: string } }>
    | undefined
  const editorial = record.editorial as
    | { scopeFeatured?: Array<{ respondent?: QualRespondent }> }
    | undefined

  const nextFeaturedRespondent = enrichRespondent(featuredQuote?.respondent, imageByKey)
  const nextThemeBlocks = Array.isArray(themeBlocks)
    ? themeBlocks.map((block) => {
        const nextRespondent = enrichRespondent(block.quote?.respondent, imageByKey)
        if (!nextRespondent || nextRespondent === block.quote?.respondent) {
          return block
        }
        return {
          ...block,
          quote: {
            ...block.quote,
            respondent: nextRespondent,
          },
        }
      })
    : themeBlocks
  const nextScopeFeatured = Array.isArray(editorial?.scopeFeatured)
    ? editorial.scopeFeatured.map((person) => {
        const nextRespondent = enrichRespondent(person.respondent, imageByKey)
        if (!nextRespondent || nextRespondent === person.respondent) {
          return person
        }
        return {
          ...person,
          respondent: nextRespondent,
        }
      })
    : editorial?.scopeFeatured

  if (
    nextFeaturedRespondent === featuredQuote?.respondent &&
    nextThemeBlocks === themeBlocks &&
    nextScopeFeatured === editorial?.scopeFeatured
  ) {
    return slide
  }

  return {
    ...slide,
    ...(featuredQuote
      ? {
          featuredQuote: {
            ...featuredQuote,
            ...(nextFeaturedRespondent
              ? { respondent: nextFeaturedRespondent }
              : {}),
          },
        }
      : {}),
    ...(nextThemeBlocks ? { themeBlocks: nextThemeBlocks } : {}),
    ...(editorial && nextScopeFeatured
      ? { editorial: { ...editorial, scopeFeatured: nextScopeFeatured } }
      : {}),
  } as SlideData
}

export function enrichDeckRespondentImages(
  deck: Deck,
  options: {
    audienceProfiles?: Array<RespondentProfile>
    responses?: Array<unknown>
    populationImageMap?: Map<string, string>
  } = {},
): Deck {
  const profiles = options.audienceProfiles ?? []
  const responses = options.responses ?? []
  const populationImageMap = options.populationImageMap

  const imageByKey = buildProfileImageMap(profiles)
  for (const [key, imageUrl] of buildResponseImageMap(responses as Array<any>)) {
    if (!imageByKey.has(key)) {
      imageByKey.set(key, imageUrl)
    }
  }
  if (populationImageMap) {
    for (const [key, imageUrl] of populationImageMap) {
      if (!imageByKey.has(key) && imageUrl) {
        imageByKey.set(key, imageUrl)
      }
    }
  }
  if (imageByKey.size === 0) {
    return deck
  }

  const slides = deck.slides.map((slide) => enrichQualAnalysisSlide(slide, imageByKey))
  const changed = slides.some((slide, index) => slide !== deck.slides[index])
  if (!changed) {
    return deck
  }
  return {
    ...deck,
    slides,
  }
}
